import { supabase } from "../../config/supabaseClient";
import { addComponent } from "./StayUpToDateDatabase";
import {
  TEMPLATE_COMPONENTS_TABLE,
  WEBSITE_TEMPLATE_SURFACE,
} from "../StayUpToDate/componentSurface";
import {
  noteWebsiteTemplateCreatedId,
  queueWebsiteTemplateComponentUpdate,
} from "../../Screens/DashboardView/MasterTemplateEditor/websiteTemplateComponentSession";
import { notifyCampaignsUpdated } from "./CampaignDatabase";
import { resolveSubscriptionEntitlements } from "../subscriptionPlan";

/** Default campaign created on first "Start editing" of the website template. */
export const DEFAULT_WEEKLY_NEWSLETTER_NAME = "Weekly Newsletter";

/** Default body copy for the first-time Weekly Newsletter campaign. */
export const DEFAULT_WEEKLY_NEWSLETTER_DESCRIPTION =
  "Notes on what I wore, what I bought, and everything I'm thinking about — every week.";

/** Email-only subscribe fields — matches the master-template Newsletter section. */
export const DEFAULT_TEMPLATE_CAMPAIGN_FIELDS = [
  { label: "Email", required: true, system: true },
] as const;

/** Default CTA label when a campaign row has no button text. */
export const DEFAULT_CAMPAIGN_BUTTON_TEXT = "Subscribe";

/** Email Campaigns always keeps an Email field, even when metadata.fields is empty. */
export function ensureCampaignEmailField<T extends { label?: string }>(
  fields: T[] | null | undefined,
): Array<T | (typeof DEFAULT_TEMPLATE_CAMPAIGN_FIELDS)[number]> {
  const list = Array.isArray(fields) ? [...fields] : [];
  const hasEmail = list.some(
    (field) => String(field?.label || "").trim().toLowerCase() === "email",
  );
  if (hasEmail) return list;
  return [...DEFAULT_TEMPLATE_CAMPAIGN_FIELDS, ...list];
}

const defaultMetadataFields = [...DEFAULT_TEMPLATE_CAMPAIGN_FIELDS];
const defaultWeeklyMetadata = {
  form_title: DEFAULT_WEEKLY_NEWSLETTER_NAME,
  description: DEFAULT_WEEKLY_NEWSLETTER_DESCRIPTION,
  fields: [...DEFAULT_TEMPLATE_CAMPAIGN_FIELDS],
};

export type CreatorCampaignRow = {
  id: string | number;
  campaign_name: string;
  campaign_button_text: string;
  image?: string | null;
  metadata?: {
    form_title?: string;
    description?: string;
    fields?: unknown[];
  } | null;
  is_active?: boolean | null;
};

export type TemplateCampaignComponentRow = {
  id: string | number;
  name?: string | null;
  display_name?: string | null;
  details?: string | null;
  route_name?: string | null;
  unique_id?: string | null;
  index?: number | null;
};

export function campaignIdKey(id: unknown): string {
  return String(id ?? "").trim();
}

function parseCampaignDetails(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function buildCampaignComponentDetails(campaign: CreatorCampaignRow): string {
  const fields =
    Array.isArray(campaign.metadata?.fields) && campaign.metadata!.fields!.length > 0
      ? campaign.metadata!.fields
      : defaultMetadataFields;
  const description = String(campaign.metadata?.description ?? "").trim();

  return JSON.stringify({
    campaign_id: campaign.id,
    campaign_name: campaign.campaign_name,
    campaign_button_text: campaign.campaign_button_text,
    image: campaign.image || "",
    description,
    fields,
    is_active: campaign.is_active ?? false,
  });
}

/**
 * Paid production Email Campaigns flag. Master template must not call this —
 * website setup must not turn on Email Campaigns for live link-in-bio.
 */
export async function enableUserActiveCampaign(userId: string): Promise<void> {
  const uid = userId.trim();
  if (!uid) return;
  await supabase.from("user").update({ active_campaign: true }).eq("user_id", uid);
}

async function addCampaignComponentToSurface(params: {
  campaign: CreatorCampaignRow;
  username: string;
  userId: string;
  from?: string;
}): Promise<{ ok: boolean; skipped?: string; error?: string; componentId?: string }> {
  const { campaign, username, userId, from } = params;
  const details = buildCampaignComponentDetails(campaign);
  const campaignName = campaign.campaign_name;
  // New campaigns stay draft on both LiB and website until the creator publishes.
  const website = from === WEBSITE_TEMPLATE_SURFACE;
  const status = "draft";
  const tryNames = [
    campaignName,
    `${campaignName} · ${campaignIdKey(campaign.id)}`,
  ];

  for (const name of tryNames) {
    const capture: { insertedRowId?: string | null } = {};
    const res = await addComponent(
      name,
      details,
      username,
      userId,
      "false",
      "",
      "",
      campaignName,
      "campaign",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      from,
      undefined,
      undefined,
      status,
      undefined,
      undefined,
      undefined,
      undefined,
      capture,
    );
    if (res === "true") {
      const componentId = String(capture.insertedRowId || "").trim();
      if (website && componentId) {
        noteWebsiteTemplateCreatedId(componentId);
      }
      return { ok: true, componentId };
    }
    if (res !== "Duplicate Name") {
      return { ok: false, error: String(res) };
    }
  }
  return { ok: true, skipped: "duplicate_component" };
}

const CAMPAIGN_LIVE_SURFACES: Array<{ from?: string; table: string }> = [
  { table: "stayuptodate_components" },
  { from: WEBSITE_TEMPLATE_SURFACE, table: TEMPLATE_COMPONENTS_TABLE },
];

async function findCampaignComponentRow(
  table: string,
  userId: string,
  campaign: CreatorCampaignRow,
): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("user_id", userId)
    .eq("type", "campaign")
    .neq("status", "deleted");
  if (error) throw new Error(error.message);
  const campaignId = campaignIdKey(campaign.id);
  const campaignName = String(campaign.campaign_name || "").trim();
  const rows = (data || []) as Array<Record<string, unknown>>;
  return (
    rows.find(
      (row) =>
        campaignIdKey(parseCampaignDetails(row.details).campaign_id) ===
        campaignId,
    ) ||
    rows.find((row) => String(row.name || "").trim() === campaignName) ||
    null
  );
}

/**
 * Link in Bio campaign rows are active immediately.
 * Website template rows stay draft until the creator publishes Pages.
 */
export async function upsertLiveCampaignOnBothSurfaces(params: {
  userId: string;
  username: string;
  campaign: CreatorCampaignRow;
}): Promise<{
  ok: boolean;
  error?: string;
  component?: Record<string, unknown>;
}> {
  const userId = params.userId.trim();
  const username = params.username.trim().toLowerCase();
  const campaign = params.campaign;
  if (!userId || !username) {
    return { ok: false, error: "missing userId or username" };
  }
  if (!campaignIdKey(campaign.id) || !String(campaign.campaign_name || "").trim()) {
    return { ok: false, error: "Campaign name is required." };
  }

  const { data: existingCampaign } = await supabase
    .from("creators_campaigns")
    .select("metadata")
    .eq("id", campaign.id)
    .eq("user_id", userId)
    .maybeSingle();
  const prevMeta =
    existingCampaign?.metadata &&
    typeof existingCampaign.metadata === "object" &&
    !Array.isArray(existingCampaign.metadata)
      ? (existingCampaign.metadata as Record<string, unknown>)
      : {};
  const metadata = {
    ...prevMeta,
    ...(campaign.metadata || {}),
    fields:
      campaign.metadata?.fields && campaign.metadata.fields.length
        ? campaign.metadata.fields
        : prevMeta.fields,
  };
  await supabase
    .from("creators_campaigns")
    .update({
      campaign_name: campaign.campaign_name,
      campaign_button_text: campaign.campaign_button_text,
      metadata,
    })
    .eq("id", campaign.id)
    .eq("user_id", userId);

  const liveCampaign: CreatorCampaignRow = {
    ...campaign,
    metadata: metadata as CreatorCampaignRow["metadata"],
  };
  const details = buildCampaignComponentDetails(liveCampaign);
  let templateComponent: Record<string, unknown> | undefined;

  try {
    for (const surface of CAMPAIGN_LIVE_SURFACES) {
      const existing = await findCampaignComponentRow(
        surface.table,
        userId,
        liveCampaign,
      );
      if (existing?.id) {
        if (surface.table === TEMPLATE_COMPONENTS_TABLE) {
          await queueWebsiteTemplateComponentUpdate(userId, existing.id, {
            details,
            display_name: liveCampaign.campaign_name,
            name: liveCampaign.campaign_name,
          });
          templateComponent = {
            ...existing,
            details,
            display_name: liveCampaign.campaign_name,
            name: liveCampaign.campaign_name,
          };
          continue;
        }
        const { error } = await supabase
          .from(surface.table)
          .update({
            details,
            status: "active",
            status_expiry: "active",
            display_name: liveCampaign.campaign_name,
          })
          .eq("id", existing.id)
          .eq("user_id", userId)
          .select("*")
          .maybeSingle();
        if (error) return { ok: false, error: error.message };
        continue;
      }
      const inserted = await addCampaignComponentToSurface({
        campaign: liveCampaign,
        username,
        userId,
        from: surface.from,
      });
      if (!inserted.ok) {
        return { ok: false, error: inserted.error || "Could not add campaign." };
      }
      if (surface.table === TEMPLATE_COMPONENTS_TABLE && inserted.componentId) {
        const { data } = await supabase
          .from(surface.table)
          .select("*")
          .eq("id", inserted.componentId)
          .eq("user_id", userId)
          .maybeSingle();
        templateComponent = (data as Record<string, unknown>) || {
          id: inserted.componentId,
          name: liveCampaign.campaign_name,
          details,
          type: "campaign",
        };
      }
    }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }

  notifyCampaignsUpdated(userId);
  return { ok: true, component: templateComponent };
}

/**
 * Soft-delete matching campaign rows from live Link in Bio only.
 * Used to undo earlier dual-link of the default Weekly Newsletter.
 * Does not touch `template_components` or the `creators_campaigns` row.
 */
async function removeCampaignFromLinkInBioSurface(params: {
  userId: string;
  campaignId: string;
}): Promise<void> {
  const userId = params.userId.trim();
  const campaignId = campaignIdKey(params.campaignId);
  if (!userId || !campaignId) return;

  const { data: components, error } = await supabase
    .from("stayuptodate_components")
    .select("id, details")
    .eq("user_id", userId)
    .eq("type", "campaign")
    .neq("status", "deleted");

  if (error || !components?.length) return;

  for (const comp of components) {
    const details = parseCampaignDetails(comp.details);
    if (campaignIdKey(details.campaign_id) !== campaignId) continue;
    await supabase
      .from("stayuptodate_components")
      .update({ status: "deleted", status_expiry: "deleted" })
      .eq("id", comp.id)
      .eq("user_id", userId);
  }
}

/**
 * After Email Campaigns creates a new `creators_campaigns` row: link it on
 * BOTH link-in-bio (`stayuptodate_components`) and website (`template_components`).
 * Does not mutate the campaign row itself.
 * Does not set `user.active_campaign` — that flag stays owned by the paid
 * production Email Campaigns flow so website setup cannot enable it.
 */
export async function linkNewCampaignToBothSurfaces(params: {
  userId: string;
  username: string;
  campaign: CreatorCampaignRow;
}): Promise<{ ok: boolean; error?: string }> {
  const userId = params.userId.trim();
  const username = params.username.trim().toLowerCase();
  if (!userId || !username) return { ok: false, error: "missing userId or username" };

  try {
    const linked = await upsertLiveCampaignOnBothSurfaces({
      userId,
      username,
      campaign: params.campaign,
    });
    if (!linked.ok) {
      return { ok: false, error: linked.error || "link failed" };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** All campaign components on the website template surface. */
export async function listTemplateCampaignComponents(
  userId: string,
): Promise<TemplateCampaignComponentRow[]> {
  const uid = userId.trim();
  if (!uid) return [];
  const { data, error } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select("id, name, display_name, details, route_name, unique_id, index")
    .eq("user_id", uid)
    .eq("type", "campaign")
    .neq("status", "deleted")
    .order("index", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as TemplateCampaignComponentRow[];
}

/**
 * Campaigns that exist as Master Template newsletter components
 * (`template_components` type=campaign). Used by Template Tweaks dropdown.
 */
export async function listTemplateLinkedCampaigns(
  userId: string,
): Promise<CreatorCampaignRow[]> {
  const uid = userId.trim();
  if (!uid) return [];

  const components = await listTemplateCampaignComponents(uid);
  const idSet = new Set<string>();
  for (const comp of components) {
    const id = campaignIdKey(parseCampaignDetails(comp.details).campaign_id);
    if (id) idSet.add(id);
  }
  if (idSet.size === 0) return [];

  const { data, error } = await supabase
    .from("creators_campaigns")
    .select("id, campaign_name, campaign_button_text, image, metadata, is_active")
    .eq("user_id", uid)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);

  return ((data ?? []) as CreatorCampaignRow[]).filter((c) =>
    idSet.has(campaignIdKey(c.id)),
  );
}

/** All email campaigns for the user (Email Campaigns + website Newsletter dropdown). */
export async function listAllUserCampaigns(
  userId: string,
): Promise<CreatorCampaignRow[]> {
  const uid = userId.trim();
  if (!uid) return [];
  const { data, error } = await supabase
    .from("creators_campaigns")
    .select("id, campaign_name, campaign_button_text, image, metadata, is_active")
    .eq("user_id", uid)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as CreatorCampaignRow[];
}

export function getConnectedCampaignIdFromComponent(
  component: TemplateCampaignComponentRow | null | undefined,
): string {
  if (!component) return "";
  return campaignIdKey(parseCampaignDetails(component.details).campaign_id);
}

/**
 * Make an existing campaign the active Newsletter source on the website template.
 * Links it to `template_components` if missing, then bumps its index to the top.
 */
export async function activateTemplateCampaign(params: {
  userId: string;
  campaignId: string;
  username?: string;
  campaign?: CreatorCampaignRow;
}): Promise<{ ok: boolean; error?: string }> {
  const userId = params.userId.trim();
  const campaignId = campaignIdKey(params.campaignId);
  if (!userId || !campaignId) return { ok: false, error: "missing ids" };

  try {
    let components = await listTemplateCampaignComponents(userId);
    let match = components.find(
      (c) => getConnectedCampaignIdFromComponent(c) === campaignId,
    );

    if (!match?.id) {
      const username = (params.username || "").trim().toLowerCase();
      let campaign = params.campaign;
      if (!campaign) {
        const { data, error } = await supabase
          .from("creators_campaigns")
          .select(
            "id, campaign_name, campaign_button_text, image, metadata, is_active",
          )
          .eq("id", campaignId)
          .eq("user_id", userId)
          .maybeSingle();
        if (error) return { ok: false, error: error.message };
        campaign = data as CreatorCampaignRow | undefined;
      }
      if (!campaign?.id || !username) {
        return {
          ok: false,
          error: "Campaign is not on the website template.",
        };
      }
      const linked = await addCampaignComponentToSurface({
        campaign,
        username,
        userId,
        from: WEBSITE_TEMPLATE_SURFACE,
      });
      if (!linked.ok) {
        return { ok: false, error: linked.error || "Could not link campaign." };
      }
      components = await listTemplateCampaignComponents(userId);
      match = components.find(
        (c) => getConnectedCampaignIdFromComponent(c) === campaignId,
      );
      if (!match?.id) {
        return { ok: false, error: "Campaign is not on the website template." };
      }
    }

    const maxIndex = components.reduce(
      (max, c) => Math.max(max, Number(c.index ?? 0)),
      0,
    );
    const { error } = await supabase
      .from(TEMPLATE_COMPONENTS_TABLE)
      .update({ index: maxIndex + 1 })
      .eq("id", match.id)
      .eq("user_id", userId);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Create a `creators_campaigns` row only — does not add a page component.
 * Used by LiB Add → Campaign so the creator can pick it, then press Add.
 */
export async function createUserEmailCampaign(params: {
  userId: string;
  campaignName: string;
  /** Optional body copy stored on campaign metadata. */
  description?: string;
  buttonText?: string;
  image?: string;
  fields?: Array<{ label?: string; required?: boolean; system?: boolean }>;
}): Promise<{ ok: boolean; campaign?: CreatorCampaignRow; error?: string }> {
  const userId = params.userId.trim();
  const campaignName = (params.campaignName || "").trim();
  const description = (params.description || "").trim();
  const buttonText =
    (params.buttonText || "").trim() || DEFAULT_CAMPAIGN_BUTTON_TEXT;
  const rawImage = String(params.image || "").trim();
  const image =
    rawImage.startsWith("blob:") || rawImage.startsWith("data:") ? "" : rawImage;
  if (!userId) {
    return { ok: false, error: "missing userId" };
  }
  if (!campaignName) {
    return { ok: false, error: "Campaign name is required" };
  }

  const metadata = {
    form_title: campaignName,
    description,
    fields: ensureCampaignEmailField(params.fields),
  };

  const { data: inserted, error: insErr } = await supabase
    .from("creators_campaigns")
    .insert({
      user_id: userId,
      campaign_name: campaignName,
      campaign_button_text: buttonText,
      image,
      is_active: false,
      metadata,
    })
    .select("id, campaign_name, campaign_button_text, image, metadata, is_active")
    .single();

  if (insErr || !inserted) {
    return { ok: false, error: insErr?.message || "campaign insert failed" };
  }
  return { ok: true, campaign: inserted as CreatorCampaignRow };
}

/**
 * Create a new email campaign from Master Template Tweaks (or other UI).
 * Writes `creators_campaigns` and links on the website (`template_components`)
 * only — never auto-adds to live Link in Bio (`stayuptodate_components`).
 * Email Campaigns UI still uses {@link linkNewCampaignToBothSurfaces} when
 * creators intentionally create from that product surface.
 */
export async function createAndLinkTemplateCampaign(params: {
  userId: string;
  username: string;
  campaignName: string;
  /** Optional website Newsletter body copy (stored on campaign metadata). */
  description?: string;
}): Promise<{ ok: boolean; campaign?: CreatorCampaignRow; error?: string }> {
  const userId = params.userId.trim();
  const username = params.username.trim().toLowerCase();
  const campaignName = (params.campaignName || "").trim();
  const description = (params.description || "").trim();
  if (!userId || !username) {
    return { ok: false, error: "missing userId or username" };
  }
  if (!campaignName) {
    return { ok: false, error: "Campaign name is required" };
  }

  const created = await createUserEmailCampaign({
    userId,
    campaignName,
    description,
  });
  if (!created.ok || !created.campaign) {
    return { ok: false, error: created.error || "campaign insert failed" };
  }

  const campaign = created.campaign;
  const linked = await addCampaignComponentToSurface({
    campaign,
    username,
    userId,
    from: WEBSITE_TEMPLATE_SURFACE,
  });
  if (!linked.ok) {
    return { ok: false, campaign, error: linked.error };
  }
  return { ok: true, campaign };
}

/**
 * Default website-template campaign is seeded only when the creator has
 * Website product UX (`hasPages`), not merely a Growth billing plan.
 */
async function isWebsiteTemplatePlan(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("user")
    .select("subscription_type, payment_plan, stripe_detail")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return true;
  return resolveSubscriptionEntitlements(data).hasPages;
}

/**
 * On first "Start editing" of the website template:
 * - create (or reuse) "Weekly Newsletter" in `creators_campaigns`
 * - link it on the website (`template_components`) only, so Home / Newsletter
 *   can subscribe. Do not add it to live Link in Bio (`stayuptodate_components`).
 * Does not turn on `user.active_campaign` (paid production Email Campaigns flag).
 * Lower plans get nothing — they have no website template and no Email Campaigns.
 */
export async function ensureDefaultWeeklyNewsletterOnStartEditing(params: {
  userId: string;
  username: string;
}): Promise<{ ok: boolean; skipped?: string; error?: string; campaignId?: string }> {
  const userId = params.userId.trim();
  const username = params.username.trim().toLowerCase();
  if (!userId || !username) {
    return { ok: false, error: "missing userId or username" };
  }

  try {
    if (!(await isWebsiteTemplatePlan(userId))) {
      return { ok: true, skipped: "plan_without_website_template" };
    }

    // Prefer an existing Weekly Newsletter campaign (do not mutate its fields).
    const { data: existingCampaign, error: findErr } = await supabase
      .from("creators_campaigns")
      .select("id, campaign_name, campaign_button_text, image, metadata, is_active")
      .eq("user_id", userId)
      .eq("campaign_name", DEFAULT_WEEKLY_NEWSLETTER_NAME)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (findErr) return { ok: false, error: findErr.message };

    let campaign: CreatorCampaignRow;
    if (existingCampaign?.id) {
      campaign = existingCampaign as CreatorCampaignRow;
      // First-time Weekly Newsletter should carry a newsletter description.
      // Backfill only when missing so we don't overwrite creator edits.
      const existingDesc = String(campaign.metadata?.description ?? "").trim();
      if (!existingDesc) {
        const nextMeta = {
          form_title:
            String(campaign.metadata?.form_title ?? "").trim() ||
            DEFAULT_WEEKLY_NEWSLETTER_NAME,
          description: DEFAULT_WEEKLY_NEWSLETTER_DESCRIPTION,
          fields:
            Array.isArray(campaign.metadata?.fields) &&
            campaign.metadata!.fields!.length > 0
              ? campaign.metadata!.fields
              : [...DEFAULT_TEMPLATE_CAMPAIGN_FIELDS],
        };
        const { error: metaErr } = await supabase
          .from("creators_campaigns")
          .update({ metadata: nextMeta })
          .eq("id", campaign.id)
          .eq("user_id", userId);
        if (!metaErr) {
          campaign = { ...campaign, metadata: nextMeta };
        }
      }
    } else {
      const { data: inserted, error: insErr } = await supabase
        .from("creators_campaigns")
        .insert({
          user_id: userId,
          campaign_name: DEFAULT_WEEKLY_NEWSLETTER_NAME,
          campaign_button_text: "Subscribe",
          image: "",
          is_active: false,
          metadata: defaultWeeklyMetadata,
        })
        .select(
          "id, campaign_name, campaign_button_text, image, metadata, is_active",
        )
        .single();

      if (insErr || !inserted) {
        return { ok: false, error: insErr?.message || "campaign insert failed" };
      }
      campaign = inserted as CreatorCampaignRow;
    }

    const linked = await addCampaignComponentToSurface({
      campaign,
      username,
      userId,
      from: WEBSITE_TEMPLATE_SURFACE,
    });
    if (!linked.ok) {
      return {
        ok: false,
        error: linked.error,
        campaignId: campaignIdKey(campaign.id),
      };
    }

    // Older bootstrap dual-linked Weekly Newsletter onto LiB — remove that
    // stayuptodate row if present. Template Newsletter link stays intact.
    await removeCampaignFromLinkInBioSurface({
      userId,
      campaignId: campaignIdKey(campaign.id),
    });

    return { ok: true, campaignId: campaignIdKey(campaign.id) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Previously turned on `user.active_campaign` when website-template access
 * started. Master template must not flip that paid production flag, so this
 * is a no-op. Kept so existing call sites do not break.
 */
export async function syncActiveCampaignWithWebsiteTemplateAccess(_params: {
  userId: string;
  hasWebsiteTemplateAccess: boolean;
}): Promise<void> {
  return;
}

/** @deprecated Prefer {@link ensureDefaultWeeklyNewsletterOnStartEditing}. */
export async function ensureDefaultTemplateCampaignOnFirstEdit(params: {
  userId: string;
  username: string;
}): Promise<{ ok: boolean; skipped?: string; error?: string }> {
  return ensureDefaultWeeklyNewsletterOnStartEditing(params);
}

/** @deprecated Prefer {@link activateTemplateCampaign} / create helpers. */
export async function linkCampaignToWebsiteTemplate(params: {
  userId: string;
  username: string;
  campaign: CreatorCampaignRow;
}): Promise<{ ok: boolean; skipped?: string; error?: string }> {
  return activateTemplateCampaign({
    userId: params.userId,
    campaignId: campaignIdKey(params.campaign.id),
  });
}
